import { useState, useEffect, useRef } from 'react';
import { estimateDeliveryFee } from '../services/api';

/**
 * useDeliveryQuote — debounced fetch to POST /orders/estimate-delivery-fee.
 *
 * @param {string} restaurantId
 * @param {{ lat: number, lng: number } | null} coords
 * @param {number} debounceMs — debounce delay (default 500ms)
 * @returns {{ quote: { fee, eta, currency } | null, outOfRange: boolean, loading: boolean, error: string | null }}
 */
export function useDeliveryQuote(restaurantId, coords, debounceMs = 500) {
  const [quote, setQuote] = useState(null);
  const [outOfRange, setOutOfRange] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!restaurantId || !coords?.lat || !coords?.lng) {
      setQuote(null);
      setOutOfRange(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      try {
        const data = await estimateDeliveryFee(restaurantId, coords.lat, coords.lng);
        if (data.outOfRange) {
          setOutOfRange(true);
          setQuote(null);
        } else {
          setOutOfRange(false);
          setQuote({
            fee: data.fee,
            eta: data.estimatedMinutes,
            currency: data.currency || '$',
          });
        }
      } catch (err) {
        // If 422 or specific error, treat as out of range
        if (err.response?.status === 422) {
          setOutOfRange(true);
          setQuote(null);
        } else {
          setError(err.message || 'Error estimating delivery');
        }
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [restaurantId, coords?.lat, coords?.lng, debounceMs]);

  return { quote, outOfRange, loading, error };
}
